/**
 * WorkflowTaskList
 * Renders task groups, each with a WorkflowGroupHeader and a list of TaskRows.
 * Collapse state is controlled by the taskUIStore `expandedWorkflows` map.
 * Missing from the map means expanded by default.
 */

import styled from 'styled-components';
import { TaskRow, EmptyTaskState, workflowTypeLabel } from '../../components/tasks';
import { useTaskUIStore } from '../../stores/taskUIStore';
import type { Task, WorkflowInstanceSummary } from '../../types/tasks';
import { WorkflowGroupHeader } from './WorkflowGroupHeader';

// ============================================================================
// Types
// ============================================================================

export interface WorkflowGroup {
  workflowInstance: WorkflowInstanceSummary;
  tasks: Task[];
}

export interface WorkflowTaskListProps {
  groups: WorkflowGroup[];
  onViewDetails?: (taskId: string) => void;
}

// ============================================================================
// Helpers
// ============================================================================

function countByStatus(tasks: Task[], status: string): number {
  return tasks.filter((t) => t.status === status).length;
}

// ============================================================================
// Styled components
// ============================================================================

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const GroupCard = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const TaskListWrapper = styled.div<{ $isExpanded: boolean }>`
  overflow: hidden;
  transition: max-height ${({ theme }) => theme.transitions.slow};
  max-height: ${({ $isExpanded }) => ($isExpanded ? '9999px' : '0px')};
`;

// ============================================================================
// Component
// ============================================================================

export function WorkflowTaskList({ groups, onViewDetails }: WorkflowTaskListProps) {
  const { expandedWorkflows, toggleWorkflow } = useTaskUIStore();

  if (groups.length === 0) {
    return <EmptyTaskState message="No tasks match the current filter." />;
  }

  return (
    <ListWrapper>
      {groups.map(({ workflowInstance, tasks }) => {
        /**
         * Default to expanded: a workflow is collapsed only when its key is
         * explicitly set to `false` in the expandedWorkflows map.
         */
        const isExpanded = expandedWorkflows[workflowInstance.id] !== false;
        const overdueCount = countByStatus(tasks, 'OVERDUE');
        const inProgressCount = countByStatus(tasks, 'IN_PROGRESS');

        return (
          <GroupCard key={workflowInstance.id}>
            <WorkflowGroupHeader
              workflowInstanceId={workflowInstance.id}
              workflowType={workflowInstance.workflowType}
              clientName={
                workflowInstance.clientMember
                  ? `${workflowInstance.clientMember.firstName} ${workflowInstance.clientMember.lastName}`
                  : null
              }
              status={workflowInstance.status}
              slaDeadline={workflowInstance.slaDeadline}
              taskCount={tasks.length}
              overdueCount={overdueCount}
              inProgressCount={inProgressCount}
              isExpanded={isExpanded}
              onToggle={() => toggleWorkflow(workflowInstance.id)}
            />
            <TaskListWrapper
              $isExpanded={isExpanded}
              role="region"
              aria-label={`Tasks for ${workflowTypeLabel(workflowInstance.workflowType)}`}
            >
              {tasks.length === 0 ? (
                <EmptyTaskState message="No tasks in this workflow." />
              ) : (
                tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    variant="scheduler"
                    showWorkflowBadge={false}
                    onViewDetails={onViewDetails}
                  />
                ))
              )}
            </TaskListWrapper>
          </GroupCard>
        );
      })}
    </ListWrapper>
  );
}

export default WorkflowTaskList;
